import { computed, ref } from "vue";

const compactModeClass = "compact-mode";
const compactModeStorageKey = "compactModePreference";
const compactModeAcceptedValue = "accepted";
const compactModeDisabledValue = "disabled";
const compactViewportQuery = "(min-width: 950px) and (max-width: 1280px), (min-width: 950px) and (max-height: 760px)";

const compactModePreference = ref(null);
const compactModeEligible = ref(false);
const compactModeEnabled = ref(false);
const compactModePromptOpen = ref(false);

let mediaQuery = null;
let initialized = false;

const getStoredCompactMode = () => {
  try {
    return window.localStorage.getItem(compactModeStorageKey);
  } catch (_) {
    return null;
  }
};

const setStoredCompactMode = (value) => {
  try {
    if (!value) {
      window.localStorage.removeItem(compactModeStorageKey);
      return;
    }

    window.localStorage.setItem(compactModeStorageKey, value);
  } catch (_) {
    // Ignore storage write failures and keep the in-memory state.
  }
};

const syncCompactMode = () => {
  const shouldEnableCompactMode = compactModePreference.value === compactModeAcceptedValue && compactModeEligible.value;

  compactModeEnabled.value = shouldEnableCompactMode;
  compactModePromptOpen.value = compactModeEligible.value && !compactModePreference.value;

  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle(compactModeClass, shouldEnableCompactMode);
  }
};

const setCompactModePreference = (value) => {
  compactModePreference.value = value;
  setStoredCompactMode(value);
  syncCompactMode();
};

export const isCompactModeEnabled = computed(() => compactModeEnabled.value);
export const shouldShowCompactModePrompt = computed(() => compactModePromptOpen.value);
export const shouldShowCompactModeToggle = computed(() => compactModeEligible.value);

export const enableCompactMode = () => setCompactModePreference(compactModeAcceptedValue);
export const disableCompactMode = () => setCompactModePreference(compactModeDisabledValue);
export const toggleCompactMode = () => {
  if (compactModeEnabled.value) {
    disableCompactMode();
    return;
  }

  enableCompactMode();
};

export default function initCompactMode() {
  if (typeof window === "undefined" || initialized) return;

  mediaQuery = window.matchMedia(compactViewportQuery);
  compactModePreference.value = getStoredCompactMode();

  const handleCompactModeChange = () => {
    compactModeEligible.value = mediaQuery.matches;
    syncCompactMode();
  };

  handleCompactModeChange();
  mediaQuery.addEventListener("change", handleCompactModeChange);
  window.addEventListener("resize", handleCompactModeChange, { passive: true });
  initialized = true;
}
